import React from 'react';
import { Button, Flex, Heading, Text } from '@vibe/core';
import { TimesheetStatusBadge } from './TimesheetStatusBadge';
import type {
  StatusHistoryEntry,
  TimesheetStatus,
} from '../../types/timesheet-approval.types';

export interface TimesheetStatusHistoryViewerProps {
  history: StatusHistoryEntry[];
  /** Map of user IDs to display names for the timeline. */
  userNameMap?: Record<string, string>;
  onClose: () => void;
}

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Modal/drawer that displays the JSONB status_history as a vertical timeline.
 */
const TimesheetStatusHistoryViewer: React.FC<
  TimesheetStatusHistoryViewerProps
> = ({ history, userNameMap, onClose }) => {
  return (
    <div className="rp-ts-dialog-overlay" onClick={onClose}>
      <div
        className="rp-ts-dialog rp-ts-dialog--wide"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex justify={Flex.justify.SPACE_BETWEEN} align={Flex.align.CENTER}>
          <Heading type={Heading.types.H3}>Status History</Heading>
          <Button
            kind={Button.kinds.TERTIARY}
            size={Button.sizes.SMALL}
            onClick={onClose}
          >
            Close
          </Button>
        </Flex>

        <div className="rp-ts-history-timeline">
          {history.length === 0 ? (
            <Text type={Text.types.TEXT2} color={Text.colors.SECONDARY}>
              No history available.
            </Text>
          ) : (
            history.map((entry, idx) => (
              <div key={idx} className="rp-ts-history-entry">
                <div className="rp-ts-history-entry__dot" />
                <div className="rp-ts-history-entry__content">
                  <Flex gap={Flex.gaps.XS} align={Flex.align.CENTER}>
                    <TimesheetStatusBadge
                      status={entry.status as TimesheetStatus}
                    />
                    <Text type={Text.types.TEXT2} color={Text.colors.SECONDARY}>
                      {formatTimestamp(entry.timestamp)}
                    </Text>
                  </Flex>
                  <Text type={Text.types.TEXT2}>
                    by{' '}
                    {userNameMap?.[entry.actor_user_id] ??
                      `User ${entry.actor_user_id}`}
                  </Text>
                  {entry.reason && (
                    <Text
                      type={Text.types.TEXT2}
                      color={Text.colors.SECONDARY}
                      style={{ fontStyle: 'italic' }}
                    >
                      {entry.reason}
                    </Text>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default TimesheetStatusHistoryViewer;
